import { useState, useEffect } from 'react';
import '../style/DocumentList.css';
import { apiService } from '../services/api'; 

const DocumentList = ({ documents, onDelete, onRowClick, onDownload, currentUserRole }) => {
  // --- FILTER STATE ---
  const [includeExpired, setIncludeExpired] = useState(false);
  const [showExpiredOnly, setShowExpiredOnly] = useState(false);
  const [showExpiringSoonOnly, setShowExpiringSoonOnly] = useState(false);
  
  // --- SORT STATE ---
  const [sortConfig, setSortConfig] = useState({ key: 'uploadedDate', direction: 'desc' });
  const [deletingId, setDeletingId] = useState(null);
  const [isExporting, setIsExporting] = useState(false); // New state for export loading
  
  // --- PAGINATION STATE ---
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10); 

  useEffect(() => {
    setCurrentPage(1);
  }, [includeExpired, showExpiredOnly, showExpiringSoonOnly, documents, sortConfig]);

  const getDaysRemaining = (expireDateString) => { 
    if (!expireDateString) return null;
    const today = new Date();
    const expireDate = new Date(expireDateString);
    today.setHours(0, 0, 0, 0);
    expireDate.setHours(0, 0, 0, 0);
    const diffTime = expireDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getExpiryStatus = (doc) => { 
    if (!doc.expireDate) return 'VALID'; 
    const diffDays = getDaysRemaining(doc.expireDate);
    if (diffDays < 0) return 'EXPIRED';
    const alertWindow = doc.alert_days_before ? Number(doc.alert_days_before) : 0; 
    if (diffDays >= 0 && diffDays <= alertWindow) return 'EXPIRING_SOON';
    return 'VALID'; 
  };

  const getExpiryLabel = (doc) => { 
    if (doc.is_archived) return { label: 'Archived', className: 'archived' }; 
    if (!doc.expireDate) return null;
    const diffDays = getDaysRemaining(doc.expireDate);
    const alertWindow = doc.alert_days_before ? Number(doc.alert_days_before) : 0;
    if (diffDays < 0) return { label: 'Expired', className: 'expired' };
    if (diffDays >= 0 && diffDays <= alertWindow) {
      const monthsRemaining = Math.ceil(diffDays / 30);
      let labelText = '';
      let className = 'expiring-warning'; 
      if (monthsRemaining <= 1) {
        labelText = 'Expiring in < 1 Month';
        className = 'expiring'; 
      } else {
        labelText = `Expiring in ${monthsRemaining} Months`;
      }
      return { label: labelText, className: className };
    }
    return null; 
  };

  const handleDownloadClick = (e, doc) => { 
    e.stopPropagation(); e.preventDefault();
    if (onDownload) onDownload(doc);
  };

  const handleDeleteClick = async (e, docId) => { 
    e.stopPropagation(); e.preventDefault();
    if (deletingId === docId) return;
    if (window.confirm("Are you sure you want to delete this document?")) {
      setDeletingId(docId); 
      if (onDelete) await onDelete(docId);
      setDeletingId(null); 
    }
  };

  const handleSort = (key) => { 
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const renderSortArrow = (key) => { 
    if (sortConfig.key !== key) return <span style={{color: '#ccc', fontSize: '0.8em', marginLeft: '4px'}}>↕</span>;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // --- NEW: EXPORT TO EXCEL HANDLER ---
  const handleExportExcel = async () => {
    try {
      setIsExporting(true);
      // Call the new API method
      await apiService.exportIndex();
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export document index.');
    } finally {
      setIsExporting(false);
    }
  };

  // --- FILTERING LOGIC ---
  let processedDocs = documents.filter(doc => {
    const status = getExpiryStatus(doc);
    if (showExpiredOnly) return status === 'EXPIRED';
    if (showExpiringSoonOnly) return status === 'EXPIRING_SOON';
    if (includeExpired) return true; 
    if (doc.is_archived) return true;
    return status !== 'EXPIRED';
  });

  // --- SORTING LOGIC ---
  if (sortConfig.key) {
    processedDocs.sort((a, b) => {
      const dateA = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
      const dateB = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
      if (dateA < dateB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (dateA > dateB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const expiringCount = processedDocs.filter(doc => getExpiryStatus(doc) === 'EXPIRING_SOON').length;
  const totalPages = Math.ceil(processedDocs.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentDocuments = processedDocs.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const handleIncludeExpiredChange = (e) => {
    const checked = e.target.checked;
    setIncludeExpired(checked);
    if (checked) { setShowExpiredOnly(false); setShowExpiringSoonOnly(false); }
  };

  const handleShowExpiredOnlyChange = (e) => {
    const checked = e.target.checked;
    setShowExpiredOnly(checked);
    if (checked) { setIncludeExpired(false); setShowExpiringSoonOnly(false); }
  };

  const handleShowExpiringSoonOnlyChange = (e) => {
    const checked = e.target.checked;
    setShowExpiringSoonOnly(checked);
    if (checked) { setIncludeExpired(false); setShowExpiredOnly(false); }
  };

  return (
    <div className="doc-list-container">
      
      {/* Notice Panel */}
      {expiringCount > 0 && !showExpiringSoonOnly && (
        <div className="doc-notice-panel">
          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
          <span><strong>Attention:</strong> There are <strong>{expiringCount}</strong> document{expiringCount !== 1 ? 's' : ''} in this list expiring soon.</span>
          <button className="doc-notice-btn" onClick={() => {
              setShowExpiringSoonOnly(true);
              setIncludeExpired(false);
              setShowExpiredOnly(false);
            }}>Filter View</button>
        </div>
      )}

      <div className="doc-list-card">
        <div className="doc-list-header">
          <div className="doc-filters-group">
            <label className="doc-switch-label">
              <input type="checkbox" className="doc-switch-input" checked={includeExpired} onChange={handleIncludeExpiredChange} />
              Include Expired
            </label>
            <label className="doc-switch-label">
              <input type="checkbox" className="doc-switch-input" checked={showExpiredOnly} onChange={handleShowExpiredOnlyChange} />
              Show Expired Only
            </label>
            <label className="doc-switch-label">
              <input type="checkbox" className="doc-switch-input" checked={showExpiringSoonOnly} onChange={handleShowExpiringSoonOnlyChange} />
              Show Expiring Soon Only
            </label>
          </div>

          <div className="doc-pagination-controls-top" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {/* NEW EXPORT BUTTON */}
            <button 
              onClick={handleExportExcel} 
              disabled={isExporting}
              style={{
                padding: '6px 12px',
                backgroundColor: '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: isExporting ? 'wait' : 'pointer',
                fontWeight: 'bold'
              }}
            >
              {isExporting ? 'Exporting...' : '📊 Export Index'}
            </button>

            <div>
              <span style={{fontSize: '0.9rem', color: '#666', marginRight: '8px'}}>Show:</span>
              <select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="doc-pagination-select">
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </div>

        <div className="doc-table-wrapper">
          <table className="doc-table">
            <thead>
              <tr>
                <th className="doc-col-name">Name</th>
                <th>Format</th>
                <th>Category</th>
                <th>Organisation</th>
                <th onClick={() => handleSort('uploadedDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Uploaded {renderSortArrow('uploadedDate')}</th>
                <th onClick={() => handleSort('startDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Start Date {renderSortArrow('startDate')}</th>
                <th onClick={() => handleSort('expireDate')} style={{ cursor: 'pointer', userSelect: 'none' }}>Expires {renderSortArrow('expireDate')}</th>
                <th style={{ width: '140px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentDocuments.length > 0 ? (
                currentDocuments.map(doc => {
                  const expiryInfo = getExpiryLabel(doc);
                  const isDeleting = deletingId === doc.id; 
                  
                  return (
                    <tr key={doc.id} onClick={() => onRowClick && onRowClick(doc)} style={{ cursor: 'pointer', opacity: isDeleting ? 0.5 : 1, pointerEvents: isDeleting ? 'none' : 'auto' }}>
                      <td><div className="doc-name-cell"><span>{doc.name}</span></div></td>
                      <td><span className="doc-format-badge">{doc.format}</span></td>
                      <td>{doc.category}</td>
                      <td>{doc.organisation}</td>
                      <td>{new Date(doc.uploadedDate).toLocaleDateString()}</td>
                      <td>{doc.startDate ? new Date(doc.startDate).toLocaleDateString() : <span className="doc-no-date">-</span>}</td>
                      <td>
                        {doc.expireDate ? (
                          <div className="doc-expiry-wrapper">
                            <span>{new Date(doc.expireDate).toLocaleDateString()}</span>
                            {expiryInfo && <span className={`doc-tag ${expiryInfo.className}`}>{expiryInfo.label}</span>}
                          </div>
                        ) : (
                          doc.is_archived ? <span className="doc-tag archived">Archived</span> : <span className="doc-no-date">-</span>
                        )}
                      </td>
                      <td>
                        <div className="doc-actions-row">
                          <button className="doc-btn-download" title="Download" onClick={(e) => handleDownloadClick(e, doc)} disabled={isDeleting}>⬇️</button>
                          {currentUserRole === 'admin' && (
                            <button className="doc-btn-delete" title="Delete" onClick={(e) => handleDeleteClick(e, doc.id)} disabled={isDeleting} style={{ cursor: isDeleting ? 'wait' : 'pointer' }}>
                              {isDeleting ? '...' : '🗑️'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr><td colSpan="8" className="doc-no-results">No documents found matching your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="doc-pagination-footer">
            <button className="doc-page-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>&lt; Prev</button>
            <span className="doc-page-info">Page {currentPage} of {totalPages}</span>
            <button className="doc-page-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next &gt;</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentList;
